import React, { FC, useState, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { currency } from '../../utils';
import { DataVisualPanel } from './DataVisualPanel';
import { Kfh_LocationAverageType } from '../../types/graphqlTypes';
import { CaretDown } from '../icon/icons/CaretDown';
import { CaretUp } from '../icon/icons/CaretUp';
import { Typography } from '../Typography';
import { Panel } from '../Panel';
import { Spacer } from '../Spacer';
import { Select } from '../forms/Select';
import { PRICE_SINCE } from '../../const';
import { StyledTableCell, StyledTableRow, StyledTableHeaderCell, Table } from '../Table';
import { SoldDataDirectionValue } from './DirectionValue';

interface AverageSoldPricesInRegionProps {
  regionName: string;
  locationName: string;
  regionSoldPrices: Kfh_LocationAverageType;
  areas: any;
}

interface SoldDataValueProps {
  heading: string;
  value: string;
  direction?: string;
  featured?: boolean;
}

const StyledMobileTable = styled.div`
  ${({ theme: { media } }) => css`
    @media (min-width: ${media.md}) {
      display: none;
    }
  `};
`;

const StyledDesktopTable = styled.div`
  ${({ theme: { maxMedia } }) => css`
    @media (max-width: ${maxMedia.md}) {
      display: none;
    }
  `};
`;

const StyledSoldDataValue = styled.div`
  ${({ theme: { maxMedia } }) => css`
    /* ... */
  `};
`;

const StyledSoldDataPanel = styled.dl`
  ${({ theme: { space, media } }) => css`
    display: flex;
    flex-wrap: wrap;
    gap: ${space.sm} ${space.lg};

    > *:first-child {
      flex-basis: 100%;
    }
  `};
`;

const StyledSelect = styled(Select)`
  ${({ theme: { space, media } }) => css`
    @media (min-width: ${media.md}) {
      width: 176px;
    }
  `};
`;

const SoldDataValue: FC<SoldDataValueProps> = ({ heading, value, direction, featured, ...rest }) => (
  <StyledSoldDataValue {...rest}>
    <Typography component="dt" variant={featured ? 'titleSmall' : 'titleExtraSmall'}>
      {heading}
    </Typography>
    <Typography component="dd" variant={featured ? 'bodyLarge' : 'bodySmall'}>
      <SoldDataDirectionValue value={value} direction={direction} />
    </Typography>
  </StyledSoldDataValue>
);

export const AverageSoldPricesInRegion: FC<AverageSoldPricesInRegionProps> = ({
  regionName,
  locationName,
  regionSoldPrices,
  areas,
  ...rest
}) => {
  const createActiveRegionAreaSoldPrices = (pricesSinceYear, orderBy, orderDirection) => {
    const selectedRegionSoldPrices = areas
      ?.map((item) => {
        const area = item?.node;
        if (!area) return null;
        const { name, id, relativeUrl } = area;
        const averageSoldPrices = area?.locationSoldDataChange?.soldDataChanges?.averages;
        const activeAverageSoldPrice = averageSoldPrices?.find(
          (averageSoldPrice) => averageSoldPrice.year === pricesSinceYear
        );
        return {
          name,
          id,
          relativeUrl,
          soldPrices: activeAverageSoldPrice,
        };
      })
      .filter((item) => !!item);

    if (!selectedRegionSoldPrices || selectedRegionSoldPrices.length === 0) {
      return null;
    }
    // Handle sort order

    if (orderBy === 'name' && orderDirection === 'asc') {
      selectedRegionSoldPrices?.sort((a, b) => a.name.localeCompare(b.name));
    } else if (orderBy === 'name' && orderDirection === 'desc') {
      selectedRegionSoldPrices?.sort((a, b) => b.name?.localeCompare(a.name));
    } else if (orderBy === 'price' && orderDirection === 'asc') {
      selectedRegionSoldPrices?.sort((a, b) => a.soldPrices?.average - b.soldPrices?.average);
    } else if (orderBy === 'price' && orderDirection === 'desc') {
      selectedRegionSoldPrices?.sort((a, b) => b.soldPrices?.average - a.soldPrices?.average);
    } else if (orderBy === 'priceChange' && orderDirection === 'asc') {
      selectedRegionSoldPrices?.sort((a, b) => a.soldPrices?.priceChange - b.soldPrices?.priceChange);
    } else if (orderBy === 'priceChange' && orderDirection === 'desc') {
      selectedRegionSoldPrices?.sort((a, b) => b.soldPrices?.priceChange - a.soldPrices?.priceChange);
    } else if (orderBy === 'percentageChange' && orderDirection === 'asc') {
      selectedRegionSoldPrices?.sort((a, b) => a.soldPrices?.percentageChange - b.soldPrices?.percentageChange);
    } else if (orderBy === 'percentageChange' && orderDirection === 'desc') {
      selectedRegionSoldPrices?.sort((a, b) => b.soldPrices?.percentageChange - a.soldPrices?.percentageChange);
    }

    return selectedRegionSoldPrices;
  };

  const initialPricesSinceYear = 1;
  const initialRegionAreaSoldPrices = useMemo(() => createActiveRegionAreaSoldPrices(1, 'name', 'asc'), []);
  const initialRegionSoldPrices = regionSoldPrices?.find((stat) => stat.year === initialPricesSinceYear);
  const [activeRegionSoldPrices, setActiveRegionSoldPrices] = useState(initialRegionSoldPrices);
  const [activeRegionAreaSoldPrices, setActiveRegionAreaSoldPrices] = useState(initialRegionAreaSoldPrices);
  const [activeRegionAreaSoldPricesOrderBy, setActiveRegionAreaSoldPricesOrderBy] = useState('name');
  const [activeRegionAreaSoldPricesOrderDirection, setActiveRegionAreaSoldPricesOrderDirection] = useState('asc');
  const [activeRegionAreaSoldPricesSinceYear, setActiveRegionAreaSoldPricesSinceYear] =
    useState(initialPricesSinceYear);

  const handlePriceSinceChange = (event) => {
    const year = parseInt(event.target.value, 10);
    setActiveRegionAreaSoldPricesSinceYear(year);
    setActiveRegionAreaSoldPrices(
      createActiveRegionAreaSoldPrices(
        year,
        activeRegionAreaSoldPricesOrderBy,
        activeRegionAreaSoldPricesOrderDirection
      )
    );
    setActiveRegionSoldPrices(regionSoldPrices?.find((stat) => stat.year === year));
  };

  const handleTableOrderChange = (orderBy) => {
    setActiveRegionAreaSoldPricesOrderBy(orderBy);
    let orderDirection = activeRegionAreaSoldPricesOrderDirection;
    if (activeRegionAreaSoldPricesOrderBy === orderBy) {
      orderDirection = activeRegionAreaSoldPricesOrderDirection === 'asc' ? 'desc' : 'asc';
      setActiveRegionAreaSoldPricesOrderDirection(orderDirection);
    }
    setActiveRegionAreaSoldPrices(
      createActiveRegionAreaSoldPrices(activeRegionAreaSoldPricesSinceYear, orderBy, orderDirection)
    );
  };

  const handleSelectOrderChange = (event) => {
    const orderBy = event.target.value;
    const orderDirection = event.target[event.target.selectedIndex].getAttribute('data-direction');
    setActiveRegionAreaSoldPricesOrderDirection(orderDirection);
    setActiveRegionAreaSoldPrices(
      createActiveRegionAreaSoldPrices(activeRegionAreaSoldPricesSinceYear, orderBy, orderDirection)
    );
  };

  const sortTableOptions = [
    {
      id: 'name',
      label: 'Area',
    },
    {
      id: 'price',
      label: 'Average price',
    },
    {
      id: 'priceChange',
      label: 'Price change',
    },
    {
      id: 'percentageChange',
      label: '% change',
    },
  ];

  const sortSelectOptions = [
    {
      id: 'name',
      label: 'Area - A - Z',
      direction: 'asc',
    },
    {
      id: 'name',
      label: 'Area - Z - A',
      direction: 'desc',
    },
    {
      id: 'price',
      label: 'Average price - Low to high',
      direction: 'asc',
    },
    {
      id: 'price',
      label: 'Average price - High to low',
      direction: 'desc',
    },
    {
      id: 'priceChange',
      label: 'Price change - Low to high',
      direction: 'asc',
    },
    {
      id: 'priceChange',
      label: 'Price change - High to low',
      direction: 'desc',
    },
    {
      id: 'percentageChange',
      label: '% change - Low to high',
      direction: 'asc',
    },
    {
      id: 'percentageChange',
      label: '% change - High to low',
      direction: 'desc',
    },
  ];

  return (
    <DataVisualPanel
      heading={`Average ${regionName} sold prices`}
      source="Land Registry"
      control={
        <div>
          <Select
            id="priceSince"
            label="Show average price since"
            onChange={handlePriceSinceChange}
            defaultValue="1"
            required
            options={PRICE_SINCE}
            {...rest}
          />
        </div>
      }
      {...rest}
    >
      <StyledMobileTable>
        {activeRegionAreaSoldPrices && (
          <>
            <Panel backgroundColor="grey1" heading={regionName}>
              <Spacer spacing="half" as="dl">
                <SoldDataValue
                  heading="Average sold price"
                  value={currency(activeRegionSoldPrices?.average) || 'No data'}
                  featured
                />
                <SoldDataValue
                  heading="Price change"
                  value={
                    activeRegionSoldPrices?.priceChange ? `${currency(activeRegionSoldPrices?.priceChange)}` : 'No data'
                  }
                  direction={activeRegionSoldPrices?.direction}
                  featured
                />
                <SoldDataValue
                  heading="% chance"
                  value={
                    activeRegionSoldPrices?.percentageChange
                      ? `${activeRegionSoldPrices?.percentageChange}%`
                      : 'No data'
                  }
                  direction={activeRegionSoldPrices?.direction}
                  featured
                />
              </Spacer>
            </Panel>
            <div>
              <StyledSelect id="sortBy" label="Sort by" onChange={handleSelectOrderChange}>
                {sortSelectOptions?.map((option, index) => (
                  <option value={option.id} data-direction={option.direction} key={index}>
                    {option.label}
                  </option>
                ))}
              </StyledSelect>
            </div>
            <Spacer spacing="half" as="ul">
              {activeRegionAreaSoldPrices.map((activeRegionSoldPrice) => (
                <Panel
                  key={activeRegionSoldPrice?.id}
                  heading={activeRegionSoldPrice?.name}
                  border
                  as="li"
                  backgroundColor={activeRegionSoldPrice?.name === locationName ? 'grey1' : 'white'}
                >
                  <StyledSoldDataPanel>
                    <SoldDataValue
                      heading="Average sold price"
                      value={currency(activeRegionSoldPrice?.soldPrices?.average) || 'No data'}
                    />
                    <SoldDataValue
                      heading="Price change"
                      value={
                        activeRegionSoldPrice?.soldPrices?.priceChange
                          ? `${currency(activeRegionSoldPrice?.soldPrices?.priceChange)}`
                          : 'No data'
                      }
                      direction={activeRegionSoldPrice?.soldPrices?.direction}
                    />
                    <SoldDataValue
                      heading="% chance"
                      value={
                        activeRegionSoldPrice?.soldPrices?.percentageChange
                          ? `${activeRegionSoldPrice?.soldPrices?.percentageChange}%`
                          : 'No data'
                      }
                      direction={activeRegionSoldPrice?.soldPrices?.direction}
                    />
                  </StyledSoldDataPanel>
                </Panel>
              ))}
            </Spacer>
          </>
        )}
      </StyledMobileTable>
      <StyledDesktopTable>
        <Table>
          <thead>
            <StyledTableRow sticky>
              {sortTableOptions?.map((option) => (
                <StyledTableHeaderCell
                  sortable
                  color={activeRegionAreaSoldPricesOrderBy === option.id ? 'brand' : 'black'}
                  key={option.id}
                >
                  <button type="button" key={option.id} onClick={() => handleTableOrderChange(option.id)}>
                    <span>{option.label}</span>
                    {activeRegionAreaSoldPricesOrderBy === option.id ? (
                      <>{activeRegionAreaSoldPricesOrderDirection === 'asc' ? <CaretUp /> : <CaretDown />}</>
                    ) : (
                      <CaretDown />
                    )}
                  </button>
                </StyledTableHeaderCell>
              ))}
            </StyledTableRow>
          </thead>
          <tbody>
            <StyledTableRow priority sticky stickyOffset="53px">
              <StyledTableCell>{regionName}</StyledTableCell>
              <StyledTableCell alignRight>{currency(activeRegionSoldPrices?.average) || 'No data'}</StyledTableCell>
              <StyledTableCell alignRight>
                <SoldDataDirectionValue
                  value={
                    activeRegionSoldPrices?.priceChange ? `${currency(activeRegionSoldPrices?.priceChange)}` : 'No data'
                  }
                  direction={activeRegionSoldPrices?.direction}
                />
              </StyledTableCell>
              <StyledTableCell alignRight>
                <SoldDataDirectionValue
                  value={
                    activeRegionSoldPrices?.percentageChange
                      ? `${activeRegionSoldPrices?.percentageChange}%`
                      : 'No data'
                  }
                  direction={activeRegionSoldPrices?.direction}
                />
              </StyledTableCell>
            </StyledTableRow>
            {activeRegionAreaSoldPrices.map((activeRegionSoldPrice) => (
              <StyledTableRow key={activeRegionSoldPrice?.id} active={activeRegionSoldPrice?.name === locationName}>
                <StyledTableCell>{activeRegionSoldPrice?.name}</StyledTableCell>
                <StyledTableCell alignRight>
                  {currency(activeRegionSoldPrice?.soldPrices?.average) || 'No data'}
                </StyledTableCell>
                <StyledTableCell alignRight>
                  <SoldDataDirectionValue
                    value={
                      activeRegionSoldPrice?.soldPrices?.priceChange
                        ? `${currency(activeRegionSoldPrice?.soldPrices?.priceChange)}`
                        : 'No data'
                    }
                    direction={activeRegionSoldPrice?.soldPrices?.direction}
                  />
                </StyledTableCell>
                <StyledTableCell alignRight>
                  <SoldDataDirectionValue
                    value={
                      activeRegionSoldPrice?.soldPrices?.percentageChange
                        ? `${activeRegionSoldPrice?.soldPrices?.percentageChange}%`
                        : 'No data'
                    }
                    direction={activeRegionSoldPrice?.soldPrices?.direction}
                  />
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </tbody>
        </Table>
      </StyledDesktopTable>
    </DataVisualPanel>
  );
};
